import { useState } from "react";
import { Alert, Button, CircularProgress, FormControlLabel, Grid, MenuItem, Switch, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useCall } from "../../provider/CallProvider";
import { apiPost } from "../../services/Api/apiCall";
import { PhoneCallRecipient } from "../../types/PhoneCall";

export default function CaseCallDialer() {
  const { callProduct, call, isConnecting, makeCall, hangUp, recordingEnabled, setRecordingEnabled,
    cancelRecording, isStoppingRecording, phoneError, status } = useCall();
  const [recipientIdentifier, setRecipientIdentifier] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const { data: recipients = [], isPending, isError, refetch } = useQuery({
    queryKey: ["caseCallRecipients", callProduct!.id],
    queryFn: () => apiPost<PhoneCallRecipient[]>("/twilio/case_call_recipients", { case: `/abstract_cases/${callProduct!.id}` }),
  });
  const selectedIdentifier = recipientIdentifier ?? recipients[0]?.identifier ?? "";
  const selectedRecipient = recipients.find((recipient) => recipient.identifier === selectedIdentifier);
  const dialedNumber = selectedIdentifier ? selectedRecipient?.number ?? "" : phoneNumber;
  const active = call !== null || isConnecting;

  return (
    <Grid container spacing={2}>
      {isPending && <Grid size={12} sx={{ textAlign: "center", py: 3 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Fallkontakte werden geladen …</Typography>
      </Grid>}
      {isError && <Grid size={12}><Alert severity="error" action={<Button onClick={() => refetch()}>Erneut laden</Button>}>Kontakte konnten nicht geladen werden.</Alert></Grid>}
      <Grid size={12}>
        <TextField select fullWidth label="Anrufen" value={selectedIdentifier} disabled={active || isPending}
          onChange={(event) => {
            setRecipientIdentifier(event.target.value);
            setRecordingEnabled(false);
          }}>
          {recipients.map((recipient) => <MenuItem key={recipient.identifier} value={recipient.identifier}>{recipient.label}</MenuItem>)}
          <MenuItem value="">Freie Rufnummer</MenuItem>
        </TextField>
      </Grid>
      <Grid size={12}>
        <TextField fullWidth label="Telefonnummer" placeholder="+49123456789" value={dialedNumber}
          slotProps={{ input: { readOnly: Boolean(selectedIdentifier) } }}
          disabled={active} onChange={(event) => setPhoneNumber(event.target.value)} />
      </Grid>
      <Grid size={12}>
        <Typography>
          {selectedRecipient?.recordingAllowed
            ? "Eine gültige Einwilligung der Mandantschaft liegt vor. Die Aufzeichnung ist zunächst ausgeschaltet."
            : "Für diesen Kontakt ist keine Aufzeichnung oder Transkription möglich."}
        </Typography>
        <FormControlLabel label="Aufzeichnung und Transkription" control={
          <Switch checked={recordingEnabled} disabled={isConnecting || isStoppingRecording || (active ? !recordingEnabled : !selectedRecipient?.recordingAllowed)}
            onChange={(_event, checked) => {
              if (active) {
                cancelRecording();
              } else {
                setRecordingEnabled(checked);
              }
            }} />
        } />
      </Grid>
      <Grid size={12}><Typography>{status}</Typography></Grid>
      {phoneError && <Grid size={12}><Alert severity="error">{phoneError}</Alert></Grid>}
      <Grid size={12}>
        {active ? <Button variant="contained" color="error" onClick={hangUp}>Auflegen</Button> :
          <Button variant="contained" disabled={isPending || !dialedNumber.trim()}
            onClick={() => makeCall(dialedNumber, selectedIdentifier || null)}>Anrufen</Button>}
      </Grid>
    </Grid>
  );
}
